/**
 * TenantHostRouter — hardens tenant URL routing.
 *
 * Rules enforced:
 *  1. On `<slug>.khaysdevices.store/` (subdomain root), render the tenant
 *     storefront directly instead of the main Index.
 *  2. On `<slug>.khaysdevices.store/store/<anything>`, collapse to `/`
 *     (the subdomain is the source of truth for tenant identity).
 *  3. On the main root domain (`khaysdevices.store` / `www.…`) visiting
 *     `/store/<slug>`, redirect to `https://<slug>.khaysdevices.store/` so
 *     the tenant URL is canonical.
 *  4. Lovable preview / unknown hosts fall through to path-based routing.
 */
import { ReactNode, lazy, useEffect } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { resolveTenantFromLocation } from "@/lib/tenantHost";

const TenantStorefront = lazy(() => import("@/pages/TenantStorefront"));

const PROD_ROOT = "khaysdevices.store";
function isProdRoot(host: string): boolean {
  return host === PROD_ROOT || host === `www.${PROD_ROOT}`;
}

/** Wrap the `/` route. If on a tenant subdomain, render TenantStorefront. */
export function HostRootSwitch({ children }: { children: ReactNode }) {
  const { slug, source } = resolveTenantFromLocation();
  if (source === "host" && slug) {
    return <TenantStorefront />;
  }
  return <>{children}</>;
}

/**
 * Wrap `/store/:slug`. On the production main root, canonicalize to the
 * tenant subdomain. On a subdomain host, collapse `/store/<x>` to `/`.
 */
export function TenantPathCanonicalizer({ children }: { children: ReactNode }) {
  const { slug: pathSlug = "" } = useParams<{ slug: string }>();
  const location = useLocation();
  const cleaned = pathSlug.trim().toLowerCase();
  const host = typeof window !== "undefined" ? window.location.hostname.toLowerCase() : "";
  const onProdRoot = isProdRoot(host);
  const { slug: subSlug, source } = resolveTenantFromLocation({ hostname: host, pathname: location.pathname });
  const shouldRedirectToSubdomain = onProdRoot && !!cleaned;

  useEffect(() => {
    if (!shouldRedirectToSubdomain) return;
    const target = `${window.location.protocol}//${cleaned}.${PROD_ROOT}/${location.search}`;
    window.location.replace(target);
  }, [shouldRedirectToSubdomain, cleaned, location.search]);

  if (shouldRedirectToSubdomain) {
    return null; // full-page redirect in flight
  }
  if (source === "host" && subSlug) {
    return <Navigate to={`/${location.search}`} replace />;
  }
  return <>{children}</>;
}
